import { useRef, useState, type FormEvent } from "react";

const franchiseTypes = [
  { id: "single-store", label: "單店加盟" },
  { id: "regional", label: "區域代理" },
  { id: "studio", label: "工作室合作" },
  { id: "other", label: "其他合作方式" },
];

const budgetOptions = [
  "港幣 20 萬以下",
  "港幣 20 萬 - 50 萬",
  "港幣 50 萬 - 100 萬",
  "港幣 100 萬以上",
];

export default function ApplicationForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("contact_alt") as string) || "";
    if (honeypot.trim().length > 0) {
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("contact_alt");
    const email = (data.get("email") as string) || "";
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setErrorMsg("請輸入有效的電子郵件地址");
      return;
    }
    setStatus("loading");
    try {
      const params = new URLSearchParams();
      data.forEach((v, k) => params.append(k, String(v)));
      const res = await fetch("https://readdy.ai/api/form/dahs96rp14a1h3mr6rm0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(text);
      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "送出失敗，請稍後再試");
      }
    } catch {
      setStatus("error");
      setErrorMsg("網路錯誤，請稍後再試");
    }
  };

  return (
    <section id="apply" className="py-16 md:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="inline-block px-6 py-2 rounded-md bg-brand-600 text-white text-lg tracking-wider">加盟申請</div>
          <h2 className="mt-5 font-serif text-3xl md:text-4xl text-brand-800">申請加盟 ISHB</h2>
          <p className="mt-4 text-brand-700/70 text-sm md:text-base">
            填妥以下資料，我們的加盟顧問會盡快與你聯絡，為你詳細介紹合作細節與加盟方案。
          </p>
        </div>

        <form
          id="franchise-form"
          ref={formRef}
          onSubmit={onSubmit}
          data-readdy-form
          className="bg-brand-50/60 border border-brand-100 rounded-lg p-6 md:p-8 space-y-5"
        >
          <input
            type="text"
            name="contact_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-honeypot"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="franchise-name" className="block text-sm font-medium text-brand-900 mb-1.5">姓名 *</label>
              <input
                id="franchise-name"
                type="text"
                name="name"
                required
                placeholder="你的姓名"
                className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="franchise-phone" className="block text-sm font-medium text-brand-900 mb-1.5">聯絡電話 *</label>
              <input
                id="franchise-phone"
                type="tel"
                name="phone"
                required
                placeholder="你的電話號碼"
                className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="franchise-email" className="block text-sm font-medium text-brand-900 mb-1.5">電郵地址 *</label>
            <input
              id="franchise-email"
              type="email"
              name="email"
              required
              placeholder="your@email.com"
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div>
            <label htmlFor="franchise-city" className="block text-sm font-medium text-brand-900 mb-1.5">現居城市／地區 *</label>
            <input
              id="franchise-city"
              type="text"
              name="city"
              required
              placeholder="例如：香港、九龍、新界"
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="franchise-type" className="block text-sm font-medium text-brand-900 mb-1.5">期望加盟方式 *</label>
              <select
                id="franchise-type"
                name="franchise_type"
                required
                defaultValue=""
                className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              >
                <option value="" disabled>請選擇加盟方式</option>
                {franchiseTypes.map((t) => (
                  <option key={t.id} value={t.label}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="franchise-budget" className="block text-sm font-medium text-brand-900 mb-1.5">預算資金 *</label>
              <select
                id="franchise-budget"
                name="budget"
                required
                defaultValue=""
                className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              >
                <option value="" disabled>請選擇預算</option>
                {budgetOptions.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="franchise-message" className="block text-sm font-medium text-brand-900 mb-1.5">自我介紹／留言（選填）</label>
            <textarea
              id="franchise-message"
              name="message"
              rows={4}
              maxLength={500}
              placeholder="請簡單介紹你的背景、美業經驗或加盟想法（最多 500 字）"
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            ></textarea>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm whitespace-nowrap cursor-pointer transition disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> 送出中...
                </>
              ) : (
                <>
                  提交加盟申請 <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>

            {status === "success" && (
              <p className="mt-3 text-sm text-brand-600 text-center">申請已送出！我們的加盟顧問會盡快與你聯絡。</p>
            )}
            {status === "error" && (
              <p className="mt-3 text-sm text-rose-500 text-center">{errorMsg}</p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}